import React, { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import MetricsOnboardingCard from '../../../../../shared/components/v3/Cards/MetricsOnboardingCard/MetricsOnboardingCard';
import { METRICS_PERIOD_SELECTED_MONTH } from '../../../../../shared/constants/metrics';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { ICircleData, ITimelineElement } from '../../../../../shared/interfaces/charts';
import { IMetricsOnboardingByPeriod } from '../../../../../shared/interfaces/metrics';
import { MetricsByCompany } from '../../../../../shared/services/api/metrics/metricsByCompany/metrics';
import { TRANSLATIONS_HOME } from '../../../../../shared/translations/home';
import { TRANSLATIONS_METRICS_NE } from '../../../../../shared/translations/metrics';
import { formatToLocaleMonthDay, getCurrentDateYYYYMMDD } from '../../../../../shared/utils/datetime/datetime';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';

import './MetricsCard.scss';

const MetricsCard = () => {
  const { company, locale } = useContext(CompanyContext);
  const [timelineChartData, setTimelineChartData] = useState<ITimelineElement[]>([]);
  const [stackedCircleChartData, setStackedCircleChartData] = useState<ICircleData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { t } = useTranslation();

  const currentDate = getCurrentDateYYYYMMDD();

  const getOnboardingMetrics = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await MetricsByCompany.getOnboardingMetricsByPeriod(
        company.id,
        METRICS_PERIOD_SELECTED_MONTH,
        currentDate
      );
      if (response) {
        handleOnboardingData(response);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingData = (onboardingInfo: IMetricsOnboardingByPeriod) => {
    if (onboardingInfo) {
      setStackedCircleChartData([
        {
          value: onboardingInfo.amountMembersRegistered,
          label: t(TRANSLATIONS_METRICS_NE.onboardingCard.referenceInvited, {
            count: onboardingInfo.amountMembersRegistered
          }),
          tooltip: t(
            onboardingInfo.amountMembersRegistered > 1
              ? TRANSLATIONS_METRICS_NE.onboardingCard.referenceInvitedTooltipPlural
              : TRANSLATIONS_METRICS_NE.onboardingCard.referenceInvitedTooltip,
            {
              users: toLocaleNumberFormat(onboardingInfo.amountMembersRegistered)
            }
          )
        },
        {
          value: onboardingInfo.amountMembersActive,
          label: t(TRANSLATIONS_METRICS_NE.onboardingCard.referenceActive, {
            count: onboardingInfo.amountMembersActive
          }),
          tooltip: t(
            onboardingInfo.amountMembersActive > 1
              ? TRANSLATIONS_METRICS_NE.onboardingCard.referenceActiveTooltipPlural
              : TRANSLATIONS_METRICS_NE.onboardingCard.referenceActiveTooltip,
            {
              users: toLocaleNumberFormat(onboardingInfo.amountMembersActive)
            }
          )
        }
      ]);

      setTimelineChartData((prevData) => [
        ...prevData,
        ...onboardingInfo.historicalMetrics.map((item) => ({
          value: item.newOnboarded,
          timeLabel: `${formatToLocaleMonthDay(item.firstDay, locale)} - ${formatToLocaleMonthDay(
            item.lastDay,
            locale
          )}`
        }))
      ]);
    }
  };

  useEffect(() => {
    setTimelineChartData([]);
    setStackedCircleChartData([]);
    getOnboardingMetrics();
  }, [company.id]);

  return (
    <MetricsOnboardingCard
      title={t(TRANSLATIONS_HOME.onboardingCard.title)}
      data={stackedCircleChartData}
      stackedCircleChartData={stackedCircleChartData}
      timelineChartData={timelineChartData}
      loading={loading}
      error={error}
      action={getOnboardingMetrics}
    />
  );
};

export default MetricsCard;
